import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getTenants } from 'app/entities/tenant/tenant.reducer';
import { getUsers } from 'app/shared/reducers/user-management';
import { getEntities as getContactDetails } from 'app/entities/contact-details/contact-details.reducer';
import { Gender } from 'app/shared/model/enumerations/gender.model';
import { BloodGroup } from 'app/shared/model/enumerations/blood-group.model';
import { LabServiceType } from 'app/shared/model/enumerations/lab-service-type.model';
import { createEntity, getEntity, reset, updateEntity } from './tenant-employee.reducer';

export const TenantEmployeeUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const tenants = useAppSelector(state => state.tenant.entities);
  const users = useAppSelector(state => state.userManagement.users);
  const contactDetails = useAppSelector(state => state.contactDetails.entities);
  const tenantEmployeeEntity = useAppSelector(state => state.tenantEmployee.entity);
  const loading = useAppSelector(state => state.tenantEmployee.loading);
  const updating = useAppSelector(state => state.tenantEmployee.updating);
  const updateSuccess = useAppSelector(state => state.tenantEmployee.updateSuccess);
  const genderValues = Object.keys(Gender);
  const bloodGroupValues = Object.keys(BloodGroup);
  const labServiceTypeValues = Object.keys(LabServiceType);

  const handleClose = () => {
    navigate(`/tenant-employee${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getTenants({}));
    dispatch(getUsers({}));
    dispatch(getContactDetails({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.dateOfBirth !== undefined && typeof values.dateOfBirth !== 'number') {
      values.dateOfBirth = Number(values.dateOfBirth);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastUpdated !== undefined && typeof values.lastUpdated !== 'number') {
      values.lastUpdated = Number(values.lastUpdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampEntry !== undefined && typeof values.timestampEntry !== 'number') {
      values.timestampEntry = Number(values.timestampEntry);
    }

    const entity = {
      ...tenantEmployeeEntity,
      ...values,
      tenant: tenants.find(it => it.id.toString() === values.tenant?.toString()),
      user: users.find(it => it.id.toString() === values.user?.toString()),
      contactCode: contactDetails.find(it => it.id.toString() === values.contactCode?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          gender: 'NA',
          bloodGroup: 'O_POS',
          labServiceType: 'RADIOLOGY',
          ...tenantEmployeeEntity,
          tenant: tenantEmployeeEntity?.tenant?.id,
          user: tenantEmployeeEntity?.user?.id,
          contactCode: tenantEmployeeEntity?.contactCode?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.tenantEmployee.home.createOrEditLabel" data-cy="TenantEmployeeCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.tenantEmployee.home.createOrEditLabel">Create or edit a TenantEmployee</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="tenant-employee-id"
                  label={translate('tinyMedicRestApp.tenantEmployee.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.name')}
                id="tenant-employee-name"
                name="name"
                data-cy="name"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.email')}
                id="tenant-employee-email"
                name="email"
                data-cy="email"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.education')}
                id="tenant-employee-education"
                name="education"
                data-cy="education"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.training')}
                id="tenant-employee-training"
                name="training"
                data-cy="training"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.experience')}
                id="tenant-employee-experience"
                name="experience"
                data-cy="experience"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.achievements')}
                id="tenant-employee-achievements"
                name="achievements"
                data-cy="achievements"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.dateOfBirth')}
                id="tenant-employee-dateOfBirth"
                name="dateOfBirth"
                data-cy="dateOfBirth"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.gender')}
                id="tenant-employee-gender"
                name="gender"
                data-cy="gender"
                type="select"
              >
                {genderValues.map(gender => (
                  <option value={gender} key={gender}>
                    {translate(`tinyMedicRestApp.Gender.${gender}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.bloodGroup')}
                id="tenant-employee-bloodGroup"
                name="bloodGroup"
                data-cy="bloodGroup"
                type="select"
              >
                {bloodGroupValues.map(bloodGroup => (
                  <option value={bloodGroup} key={bloodGroup}>
                    {translate(`tinyMedicRestApp.BloodGroup.${bloodGroup}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.active')}
                id="tenant-employee-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.cursor')}
                id="tenant-employee-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.lastUpdated')}
                id="tenant-employee-lastUpdated"
                name="lastUpdated"
                data-cy="lastUpdated"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.microSec')}
                id="tenant-employee-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.timestampEntry')}
                id="tenant-employee-timestampEntry"
                name="timestampEntry"
                data-cy="timestampEntry"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.exited')}
                id="tenant-employee-exited"
                name="exited"
                data-cy="exited"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.tenantEmployee.labServiceType')}
                id="tenant-employee-labServiceType"
                name="labServiceType"
                data-cy="labServiceType"
                type="select"
              >
                {labServiceTypeValues.map(labServiceType => (
                  <option value={labServiceType} key={labServiceType}>
                    {translate(`tinyMedicRestApp.LabServiceType.${labServiceType}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                id="tenant-employee-tenant"
                name="tenant"
                data-cy="tenant"
                label={translate('tinyMedicRestApp.tenantEmployee.tenant')}
                type="select"
              >
                <option value="" key="0" />
                {tenants
                  ? tenants.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="tenant-employee-user"
                name="user"
                data-cy="user"
                label={translate('tinyMedicRestApp.tenantEmployee.user')}
                type="select"
              >
                <option value="" key="0" />
                {users
                  ? users.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="tenant-employee-contactCode"
                name="contactCode"
                data-cy="contactCode"
                label={translate('tinyMedicRestApp.tenantEmployee.contactCode')}
                type="select"
              >
                <option value="" key="0" />
                {contactDetails
                  ? contactDetails.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/tenant-employee" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default TenantEmployeeUpdate;
